/**
 * ⟦SPEC-PRODUCTEUR §1.4 / §5.5 · A-81⟧ **Le récapitulatif complet d'une délégation** — les cinq
 * lignes que le recompte du 20/08 trouvait « jamais écrites » : `argent` avec ses causes,
 * `abonnes` avec `gagnes` et `perdus` séparés, `faits` (liste FERMÉE de kinds), `jauges`
 * (avant / après + décomposition ligne à ligne), `videos_detail`.
 *
 * ── Ce que ce module N'EST PAS ─────────────────────────────────────────────────
 *
 * Ce n'est ni un calcul, ni une source de vérité. **Trois des cinq lignes existaient déjà
 * sous un autre nom** : le récapitulatif ⟦A-16⟧ de chaque action jouée (`ResolveResult.recap`,
 * `engine/recap.ts`) porte DÉJÀ la cause de chaque delta de chaque jauge — argent, abonnés,
 * énergie, santé mentale… — mesurée sur l'état, jamais dérivée. Il était simplement **jeté**
 * par `laisser_tourner`, qui ne gardait des vingt actions que leurs `events` et leur `log`.
 * Ce module ne fait que **le laisser remonter**, agrégé par cause sur la durée déléguée.
 *
 * Même parti pris qu'⟦A-16⟧ : si une ligne affiche −45 ₵ « les salaires », c'est que l'état a
 * perdu 45 ₵ sous la mesure `salaires`. Aucune formule ici ne peut diverger d'une formule du
 * moteur, puisqu'il n'y en a aucune.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * Purement observationnel : aucune jauge touchée, aucun tirage consommé, aucun ordre modifié.
 * Tout ce qui est lu ici l'est APRÈS la résolution. Pureté : aucune I/O, aucune horloge.
 */
import { MAX_ACTIONS_DELEGUEES } from "./producteur.js";
import { estFaitDelegue } from "./producteur.js";
import { RECAP_JAUGES } from "./recap.js";
/**
 * Les deltas d'UNE jauge, **agrégés par cause**, dans l'ordre de première apparition — O(causes),
 * jamais O(vidéos × jours), comme §5.5 l'exige. Un delta nul n'existe pas en entrée (`recap.ts`
 * ne pousse jamais de zéro), donc une cause présente a toujours pesé.
 */
export function lignesParCause(lignes, jauge) {
    const parCause = new Map();
    for (const l of lignes) {
        if (l.jauge !== jauge)
            continue;
        parCause.set(l.cause, (parCause.get(l.cause) ?? 0) + l.delta);
    }
    return [...parCause.entries()].map(([cause, delta]) => ({ cause, delta }));
}
/**
 * Toutes les jauges qui ont bougé — ou qui portent au moins une ligne (deux causes peuvent
 * s'annuler : « −10 le travail, +10 la nuit » mérite d'être lu, A-16). Une jauge immobile et
 * sans ligne n'est pas listée : la table ne montre jamais des zéros.
 */
export function jaugesDeleguees(lignes, avant, apres) {
    const resultat = [];
    for (const jauge of RECAP_JAUGES) {
        const causes = lignesParCause(lignes, jauge);
        if (causes.length === 0 && Math.abs(apres[jauge] - avant[jauge]) < 1e-9)
            continue;
        resultat.push({ jauge, avant: avant[jauge], apres: apres[jauge], lignes: causes });
    }
    return resultat;
}
/**
 * ⟦§1.4⟧ `gagnes` / `perdus` sur les abonnés : la somme des deltas positifs, la somme des
 * négatifs — deux nombres que le solde `apres − avant` écrase, et le second est **le prix
 * structurel de la délégation** (§7.2 n° 5, les désabonnements), invisible jusqu'ici.
 */
export function abonnesDelegues(lignes) {
    let gagnes = 0;
    let perdus = 0;
    for (const l of lignes) {
        if (l.jauge !== "abonnes")
            continue;
        if (l.delta > 0)
            gagnes += l.delta;
        else
            perdus -= l.delta;
    }
    return { gagnes, perdus };
}
/**
 * ⟦§5.5⟧ Les faits conservés **un par un**, dans l'ordre chronologique, sur la liste FERMÉE
 * `FAITS_DELEGUES`. Borne dure `MAX_ACTIONS_DELEGUEES` ⟦A38⟧ : au-delà, on garde **les plus
 * récents** — le dernier fait est toujours l'avertissement d'arrêt, celui qui dit pourquoi la
 * main est rendue, et c'est lui qu'on ne peut pas perdre.
 */
export function faitsDelegues(events) {
    const faits = events.filter((e) => estFaitDelegue(e.kind));
    return faits.length > MAX_ACTIONS_DELEGUEES ? faits.slice(faits.length - MAX_ACTIONS_DELEGUEES) : faits;
}
/**
 * ⟦§1.4⟧ Les vidéos produites pendant la délégation — lues dans l'état d'arrivée, jamais
 * reconstruites depuis les événements : les ids sont incrémentaux par run (`VideoRecord.id`),
 * « produite pendant » se lit donc `id > dernier id d'avant`.
 */
export function videosDeleguees(state, idVideoAvant) {
    return state.videos
        .filter((v) => v.id > idVideoAvant)
        .sort((a, b) => a.id - b.id)
        .map((v) => ({
        id: v.id,
        subject_id: v.subject_id,
        format: v.format,
        classe: v.class,
        execution_score: v.execution_score,
    }));
}
//# sourceMappingURL=delegation-recap.js.map